import React, { useCallback, useContext, useMemo } from 'react'
import { useHistory } from 'react-router'
import styled from 'styled-components'
import { _, colors } from 'utils'
import { $gray_600 } from 'utils/colors'
import DataGrid, { ColumnDataGrid, DataGridProps } from 'components/DataGrid'
import CollapsiblePanel from 'components/CollapsiblePanel'
import Icon from 'components/Icon'
import { EventType, UserType } from 'generated/graphql'
import routes from 'routes'
import { OrganizationContext } from '../Organization'

const S = {
    Body: styled.div`
        width: 100%;
        padding: 0 20px;
    `,
    InfoWrapper: styled.div`
        height: 32px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        min-width: 0;
        max-width: 900px;
        overflow: hidden;
        padding-left: 8px;
    `,
    Title: styled.div`
        height: 16px;
        overflow: hidden;
        font-weight: 500;
        font-size: 14px;
        line-height: 16px;
        color: ${colors.grayTitle};
    `,
    SubTitle: styled.div`
        height: 16px;
        color: ${$gray_600} !important;
        font-size: 14px;
        line-height: 16px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        > {
            * {
                display: inline;
            }
            br {
                display: none;
            }
        }
    `,
    Action: styled.div`
        text-align: right;
        white-space: nowrap;
        padding-right: 8px;
    `,
}

const getRemainingDays = (event): number => {
    if (!event.firstPublishedAt || !event.startDate || !event.endDate) {
        return 0
    }
    const millisecondsPerDay = 1000 * 60 * 60 * 24
    let remaining = Math.floor((new Date(event.endDate).getTime() - Date.now()) / millisecondsPerDay)
    remaining = remaining < 0 ? 0 : remaining
    return remaining
}

const createColumns = ({ onEdit }): ColumnDataGrid[] => [
    {
        name: 'name',
        width: 7,
        render: (event: EventType) => (
            <S.InfoWrapper>
                <S.Title>
                    {event.name}
                </S.Title>
                {event.description && (
                    <S.SubTitle dangerouslySetInnerHTML={{ __html: event.description }} />
                )}
            </S.InfoWrapper>
        )
    },
    {
        name: 'day',
        width: 3,
        render: (event: EventType) => {
            const remaining = getRemainingDays(event)
            if (remaining > 0) {
                return (
                    <S.InfoWrapper style={{ alignItems: 'center' }}>
                        <S.SubTitle>
                            {_('organization.events.days_left', { remaining })}
                        </S.SubTitle>
                        {event.description && (
                            <S.SubTitle>{event.endDate}</S.SubTitle>
                        )}
                    </S.InfoWrapper>
                )
            }
            return (
                <S.InfoWrapper style={{ alignItems: 'center' }}>
                    <S.SubTitle>
                        <Icon icon="calendar" size={18} />
                        &nbsp;
                        {`${event.startDate} - ${event.endDate}`}
                    </S.SubTitle>
                </S.InfoWrapper>
            )
        }
    },
    {
        name: 'actions',
        width: 2,
        render: (event: EventType) => (
            <S.Action>
                <Icon icon="edit" className="ml-2" size={20} onClick={() => onEdit(event)} />
            </S.Action>
        )

    }
]

type EventDataGridProps = Omit<DataGridProps, 'data'|'columns'> & {
    title: string
    events: Partial<UserType>[]
    onEdit: (event: EventType) => void
}
const EventDataGrid = ({
    title,
    events,
    onEdit,
    ...props
}: EventDataGridProps) => {
    const columns = useMemo(() => createColumns({ onEdit }), [onEdit])

    return (
        <CollapsiblePanel title={title}>
            <DataGrid
                data={events}
                columns={columns}
                hideHeader
                hideAction
                {...props}
            />
        </CollapsiblePanel>
    )
}

const Events = () => {
    const { events = [] } = useContext(OrganizationContext)
    const history = useHistory()

    const upcomingEvents = useMemo(() => events.filter((event) => getRemainingDays(event) > 0), [events])
    const pastEvents = useMemo(() => events.filter((event) => getRemainingDays(event) <= 0), [events])
    const handleEditEvent = useCallback(async (event) => {
        history.push(routes.generalSettings.getPath({ eventSlug: event?.slug }))
    }, [history])

    return (
        <S.Body>
            <EventDataGrid
                events={upcomingEvents}
                title={_('organization.events.upcoming_events')}
                onEdit={handleEditEvent}
            />
            <br />
            <EventDataGrid
                events={pastEvents}
                title={_('organization.events.past_events')}
                onEdit={handleEditEvent}
            />
        </S.Body>
    )
}

export default Events
